/**
 * Settings ▸ Projects — curated repo sets, each living in exactly one space.
 *
 * Promoted out of the GitHub page (workspace-organization spec §3). The page
 * owns the data loads the GitHub page used to provide (taxonomy dimensions,
 * spaces, repo inventory) and hosts the existing ProjectsSection editor.
 */

import { useEffect, useState } from 'react';
import { FolderKanban } from 'lucide-react';
import { ProjectsSection } from '@/components/settings/ProjectsSection';
import { projectsApi, type TaxonomyDimension } from '@/services/projectsApi';
import { githubApi, type Repo } from '@/services/githubApi';
import { spacesApi, type Space } from '@/services/spacesApi';
import { useTenant } from '@/contexts/TenantContext';

export default function ProjectsSettingsPage() {
  const { tenant } = useTenant();
  const isAdmin = ['admin', 'owner'].includes(tenant?.role ?? '');

  const [dimensions, setDimensions] = useState<TaxonomyDimension[]>([]);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [repos, setRepos] = useState<Repo[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    projectsApi
      .getTaxonomy()
      .then(setDimensions)
      .catch(() => setDimensions([]));
    spacesApi
      .listSpaces()
      .then(setSpaces)
      .catch(() => setSpaces([]));
    githubApi
      .listRepos()
      .then(setRepos)
      .catch(() => setRepos([]));
  }, [isAdmin]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <FolderKanban className="w-5 h-5 text-slate-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Projects</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Curated sets of repositories. A project lives in one space — the space decides who
            sees it.
          </p>
        </div>
      </div>

      {isAdmin ? (
        <ProjectsSection dimensions={dimensions} spaces={spaces} repos={repos} />
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          Projects are managed by workspace admins. Pick your project from the selector in the
          header.
        </p>
      )}
    </div>
  );
}
